import { useState, useEffect, useMemo } from 'react';
import { X, Filter, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import type { FieldFilterOperator, FieldFilterCondition, FieldFilterConfig } from '@/types';

export type { FieldFilterOperator, FieldFilterCondition, FieldFilterConfig };

export type FieldFilterType = 'project' | 'costCentre' | 'customer' | 'costCentreTask';

export interface CustomFieldOption {
  value: string;
  label: string;
  fieldType: string;
  options?: string | null;
}

interface FieldFilterDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fieldType: FieldFilterType;
  fieldLabel?: string;
  currentFilter?: FieldFilterConfig;
  onApply: (filter: FieldFilterConfig) => void;
  customFieldDefinitions?: CustomFieldOption[];
  loadingCustomFields?: boolean;
}

const PROJECT_FILTER_FIELDS = [
  { value: 'sortKey',   label: 'Sort Key' },
  { value: 'userSort1', label: 'User Sort 1' },
  { value: 'userSort2', label: 'User Sort 2' },
  { value: 'userSort3', label: 'User Sort 3' },
  { value: 'userSort4', label: 'User Sort 4' },
  { value: 'userSort5', label: 'User Sort 5' },
  { value: 'userSort6', label: 'User Sort 6' },
  { value: 'userChar1', label: 'User Char 1' },
  { value: 'userChar2', label: 'User Char 2' },
  { value: 'userChar3', label: 'User Char 3' },
  { value: 'userChar4', label: 'User Char 4' },
  { value: 'userChar5', label: 'User Char 5' },
  { value: 'userChar6', label: 'User Char 6' },
];

const COST_CENTRE_FILTER_FIELDS = [
  { value: 'code',          label: 'Code' },
  { value: 'name',          label: 'Name' },
  { value: 'userSubtotal',  label: 'User Subtotal' },
];

const CUSTOMER_FILTER_FIELDS = [
  { value: 'code',           label: 'Code' },
  { value: 'name',           label: 'Name' },
  { value: 'region',         label: 'Region' },
  { value: 'accountManager', label: 'Account Manager' },
  { value: 'userSort1',      label: 'User Sort 1' },
  { value: 'userSort2',      label: 'User Sort 2' },
  { value: 'userChar1',      label: 'User Char 1' },
  { value: 'userChar2',      label: 'User Char 2' },
  { value: 'userChar3',      label: 'User Char 3' },
];

const COST_CENTRE_TASK_FILTER_FIELDS = [
  { value: 'code',      label: 'Code' },
  { value: 'name',      label: 'Name' },
  { value: 'taskType',  label: 'Task Type' },
  { value: 'userChar1', label: 'User Char 1' },
  { value: 'userChar2', label: 'User Char 2' },
];

const FILTER_FIELDS_BY_TYPE: Record<FieldFilterType, { value: string; label: string }[]> = {
  project:       PROJECT_FILTER_FIELDS,
  costCentre:    COST_CENTRE_FILTER_FIELDS,
  customer:      CUSTOMER_FILTER_FIELDS,
  costCentreTask: COST_CENTRE_TASK_FILTER_FIELDS,
};

const DEFAULT_FIELD_LABELS: Record<FieldFilterType, string> = {
  project:       'Project',
  costCentre:    'Cost Centre',
  customer:      'Customer',
  costCentreTask: 'Cost Centre Task',
};

export const OPERATOR_LABELS: Record<FieldFilterOperator, string> = {
  'equals':       'Equals',
  'not-equals':   'Not Equals',
  'contains':     'Contains',
  'not-contains': 'Does Not Contain',
  'starts-with':  'Starts With',
  'ends-with':    'Ends With',
  'is-empty':     'Is Empty',
  'is-not-empty': 'Is Not Empty',
  'greater-than': 'Greater Than',
  'less-than':    'Less Than',
};

type CustomFieldType = 'text' | 'number' | 'currency' | 'date' | 'boolean' | 'select';

const OPERATORS_BY_FIELD_TYPE: Record<CustomFieldType | 'default', FieldFilterOperator[]> = {
  default:  ['equals', 'not-equals', 'contains', 'not-contains', 'starts-with', 'ends-with', 'is-empty', 'is-not-empty'],
  text:     ['equals', 'not-equals', 'contains', 'not-contains', 'starts-with', 'ends-with', 'is-empty', 'is-not-empty'],
  currency: ['equals', 'not-equals', 'contains', 'not-contains', 'starts-with', 'ends-with', 'is-empty', 'is-not-empty'],
  number:   ['equals', 'not-equals', 'greater-than', 'less-than', 'is-empty', 'is-not-empty'],
  date:     ['equals', 'not-equals', 'greater-than', 'less-than', 'is-empty', 'is-not-empty'],
  boolean:  ['equals'],
  select:   ['equals', 'not-equals', 'is-empty', 'is-not-empty'],
};

const EMPTY_CONDITION: FieldFilterCondition = {
  fieldName: '',
  operator: 'contains',
  value: '',
};

function parseSelectOptions(optionsJson: string | null | undefined): { value: string; label: string }[] {
  if (!optionsJson) return [];
  try {
    const parsed = JSON.parse(optionsJson);
    if (Array.isArray(parsed)) {
      return parsed.map((opt: string | { value?: string; label?: string }) => {
        if (typeof opt === 'string') return { value: opt, label: opt };
        return { value: opt.value ?? '', label: opt.label ?? opt.value ?? '' };
      });
    }
    return [];
  } catch {
    return [];
  }
}

export function FieldFilterDialog({
  open,
  onOpenChange,
  fieldType,
  fieldLabel,
  currentFilter,
  onApply,
  customFieldDefinitions,
  loadingCustomFields,
}: FieldFilterDialogProps) {
  const [condition, setCondition] = useState<FieldFilterCondition>({ ...EMPTY_CONDITION });

  const baseFields = FILTER_FIELDS_BY_TYPE[fieldType];
  const hasCustomFields = fieldType === 'project' && customFieldDefinitions && customFieldDefinitions.length > 0;
  const resolvedFieldLabel = fieldLabel ?? DEFAULT_FIELD_LABELS[fieldType];

  const customFieldMap = useMemo(() => {
    const map = new Map<string, CustomFieldOption>();
    if (customFieldDefinitions) {
      for (const def of customFieldDefinitions) {
        map.set(def.value, def);
      }
    }
    return map;
  }, [customFieldDefinitions]);

  const selectedCustomField = customFieldMap.get(condition.fieldName);
  const selectedFieldType: CustomFieldType | 'default' = selectedCustomField
    ? (selectedCustomField.fieldType as CustomFieldType)
    : 'default';

  const availableOperators = OPERATORS_BY_FIELD_TYPE[selectedFieldType] ?? OPERATORS_BY_FIELD_TYPE.default;

  // Initialise from existing filter each time dialog opens
  useEffect(() => {
    if (open) {
      if (currentFilter?.enabled && currentFilter.conditions.length > 0) {
        setCondition({ ...currentFilter.conditions[0] });
      } else {
        setCondition({ ...EMPTY_CONDITION });
      }
    }
  }, [open, currentFilter]);

  // Reset operator when switching to a field with different available operators
  useEffect(() => {
    if (condition.fieldName && !availableOperators.includes(condition.operator)) {
      setCondition(prev => ({
        ...prev,
        operator: availableOperators[0],
        value: '',
      }));
    }
  }, [condition.fieldName, condition.operator, availableOperators]);

  const needsValue = (op: FieldFilterOperator) =>
    op !== 'is-empty' && op !== 'is-not-empty';

  const handleOperatorChange = (op: FieldFilterOperator) => {
    setCondition(prev => ({
      ...prev,
      operator: op,
      value: needsValue(op) ? prev.value : '',
    }));
  };

  const isValid = condition.fieldName !== '';

  const handleApply = () => {
    if (!isValid) {
      onApply({ enabled: false, conditions: [], logicOperator: 'AND' });
    } else {
      onApply({
        enabled: true,
        conditions: [condition],
        logicOperator: 'AND',
      });
    }
    onOpenChange(false);
  };

  const handleClear = () => {
    setCondition({ ...EMPTY_CONDITION });
  };

  const allFields = hasCustomFields ? customFieldDefinitions! : baseFields;
  const selectedFieldLabel = allFields.find(f => f.value === condition.fieldName)?.label;
  const isDisabled = !!loadingCustomFields;

  const renderValueInput = () => {
    if (!needsValue(condition.operator)) {
      return (
        <Input
          id="filter-value"
          value=""
          disabled
          placeholder="Not required for this condition"
          className="h-9"
        />
      );
    }

    if (selectedFieldType === 'boolean') {
      return (
        <Select
          value={condition.value}
          onValueChange={(v) => setCondition(prev => ({ ...prev, value: v }))}
          disabled={isDisabled}
        >
          <SelectTrigger id="filter-value" className="h-9">
            <SelectValue placeholder="Select value…" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="true">Yes</SelectItem>
            <SelectItem value="false">No</SelectItem>
          </SelectContent>
        </Select>
      );
    }

    if (selectedFieldType === 'select' && selectedCustomField) {
      const selectOptions = parseSelectOptions(selectedCustomField.options);
      if (selectOptions.length > 0) {
        return (
          <Select
            value={condition.value}
            onValueChange={(v) => setCondition(prev => ({ ...prev, value: v }))}
            disabled={isDisabled}
          >
            <SelectTrigger id="filter-value" className="h-9">
              <SelectValue placeholder="Select value…" />
            </SelectTrigger>
            <SelectContent>
              {selectOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      }
    }

    return (
      <Input
        id="filter-value"
        type={selectedFieldType === 'number' || selectedFieldType === 'currency' ? 'number' : selectedFieldType === 'date' ? 'date' : 'text'}
        value={condition.value}
        onChange={(e) => setCondition(prev => ({ ...prev, value: e.target.value }))}
        placeholder="Enter filter value…"
        className="h-9"
        disabled={isDisabled}
      />
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        {/* Header */}
        <DialogHeader>
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 flex-shrink-0">
              <Filter className="h-4 w-4 text-primary" />
            </div>
            <div>
              <DialogTitle className="text-base">
                Set Field Filter
                <span className="ml-2 text-sm font-normal text-muted-foreground">
                  — {resolvedFieldLabel}
                </span>
              </DialogTitle>
              <DialogDescription className="text-xs mt-0.5">
                Restrict which {resolvedFieldLabel.toLowerCase()} records appear in the booking
                form dropdown based on a field value.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Loading state for custom fields */}
        {loadingCustomFields && (
          <div className="flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-3 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
            Loading custom field definitions…
          </div>
        )}

        {/* Body — single condition row */}
        <div className="space-y-5 py-2">
          {/* Field */}
          <div className="space-y-1.5">
            <Label htmlFor="filter-field" className="text-sm font-medium">
              Field
            </Label>
            <Select
              value={condition.fieldName}
              onValueChange={(v) => setCondition(prev => ({ ...prev, fieldName: v, value: '' }))}
              disabled={isDisabled}
            >
              <SelectTrigger id="filter-field" className="h-9">
                <SelectValue placeholder="Select a field to filter on…" />
              </SelectTrigger>
              <SelectContent>
                {hasCustomFields ? (
                  customFieldDefinitions!.map((f) => (
                    <SelectItem key={f.value} value={f.value}>
                      {f.label}
                    </SelectItem>
                  ))
                ) : (
                  baseFields.map((f) => (
                    <SelectItem key={f.value} value={f.value}>
                      {f.label}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Operator */}
          <div className="space-y-1.5">
            <Label htmlFor="filter-operator" className="text-sm font-medium">
              Condition
            </Label>
            <Select
              value={condition.operator}
              onValueChange={(v) => handleOperatorChange(v as FieldFilterOperator)}
              disabled={isDisabled}
            >
              <SelectTrigger id="filter-operator" className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableOperators.map((op) => (
                  <SelectItem key={op} value={op}>
                    {OPERATOR_LABELS[op]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Value */}
          <div className="space-y-1.5">
            <Label htmlFor="filter-value" className="text-sm font-medium">
              Value
            </Label>
            {renderValueInput()}
          </div>

          {/* Live preview */}
          {isValid && (
            <div className="flex items-center gap-1.5 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
              <Filter className="h-3 w-3 flex-shrink-0 text-primary" />
              <span>
                Show{' '}
                <span className="font-medium text-foreground">{resolvedFieldLabel}</span>{' '}
                where{' '}
                <span className="font-medium text-foreground">{selectedFieldLabel}</span>{' '}
                {OPERATOR_LABELS[condition.operator].toLowerCase()}
                {needsValue(condition.operator) && condition.value && (
                  <>
                    {' '}
                    <span className="font-medium text-foreground">"{condition.value}"</span>
                  </>
                )}
              </span>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 pt-2">
          <Button variant="outline" onClick={handleClear} className="gap-1.5">
            <X className="h-4 w-4" />
            Clear
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleApply} disabled={isDisabled} className="gap-2">
            <Filter className="h-4 w-4" />
            Apply Filter
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
